import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import { SITE_BG, SURFACE } from "../../../dark-theme/palette";
import { decryptText } from "../../../services/CryptoUtil";
import Logger from "../../../services/Logger";
import Item from "../../components/Item";
import BrowseStylePuzzleCard, { BROWSE_SHELF_CARD_WIDTH } from "../../browse-puzzles/BrowseStylePuzzleCard";
import StaticCrossGrid from "./StaticCrossGrid";
import StaticCrossPuzzleMain from "./StaticCrossPuzzleMain";
import StaticCrossWordsPanelSection from "./StaticCrossWordsPanelSection";
import StaticToolbar from "./StaticToolbar";
import encryptedSampleDataEn from "./samples/sample_en_cross_encrypted.json";
import encryptedSampleDataFr from "./samples/sample_fr_cross_encrypted.json";
import crosswordHowToBrowseStories from "./samples/crosswordHowToBrowseStories.json";
import { SUPPORT_FRENCH } from "../../../config/supportFrench";
import { usePagesTranslation } from "../../locales";
import WordDirectionsSection from "../../login/directions/WordDirectionsSection";
import CrosswordScrambleRack from "../../puzzles/crossword/components/CrosswordScrambleRack";
import { fetchPublishedHowtoStory } from "../fetchPublishedHowtoStory";

/**
 * Crossword how-to (puzzle play only). Routed from `/howto/puzzle/crossword`.
 */
export default function CrosswordHowTo() {
    const { t, lang } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [, setSamplePuzzleData] = useState([]);
    const [samplePuzzle, setSamplePuzzle] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState("english");
    const [loadingLanguage, setLoadingLanguage] = useState(false);

    const extractEncryptedText = (data) => {
        if (typeof data === "string") return data;
        if (Array.isArray(data)) return data.join("") || data[0];
        if (typeof data === "object" && data !== null) {
            return data.data || data.encrypted || data.text || data.default || Object.values(data)[0];
        }
        return null;
    };

    useEffect(() => {
        let cancelled = false;
        setLoadingLanguage(true);
        setSamplePuzzle(null);

        const loadAndDecryptData = async () => {
            try {
                const encryptedSampleData =
                    selectedLanguage === "french" ? encryptedSampleDataFr : encryptedSampleDataEn;
                if (!encryptedSampleData) {
                    if (!cancelled) setSamplePuzzle(null);
                    return;
                }
                const encryptedText = extractEncryptedText(encryptedSampleData);
                if (!encryptedText || typeof encryptedText !== "string") {
                    if (!cancelled) setSamplePuzzle(null);
                    return;
                }
                const decryptedText = decryptText(encryptedText.trim());
                if (decryptedText && !cancelled) {
                    const parsedData = JSON.parse(decryptedText);
                    setSamplePuzzleData(parsedData);

                    let nextPuzzle = null;
                    if (parsedData && Array.isArray(parsedData) && parsedData.length > 0) {
                        nextPuzzle = parsedData[0];
                    } else if (parsedData && typeof parsedData === "object" && !Array.isArray(parsedData)) {
                        nextPuzzle = parsedData;
                    }

                    if (nextPuzzle) {
                        const overlayStory =
                            crosswordHowToBrowseStories?.[
                                selectedLanguage === "french" ? "french" : "english"
                            ];
                        if (overlayStory) {
                            nextPuzzle = { ...nextPuzzle, story: overlayStory };
                        }
                    }

                    if (!cancelled) {
                        setSamplePuzzle(nextPuzzle);
                        setLoadingLanguage(false);
                    }

                    if (nextPuzzle) {
                        const contentLang = selectedLanguage === "french" ? "french" : "english";
                        const liveStory = await fetchPublishedHowtoStory("CROSSWORD", contentLang);
                        if (!cancelled && liveStory) {
                            setSamplePuzzle({ ...nextPuzzle, story: liveStory });
                        }
                    }
                } else if (!cancelled) {
                    setSamplePuzzle(null);
                }
            } catch (error) {
                Logger.error("Error decrypting or parsing sample data:", error);
                if (!cancelled) setSamplePuzzle(null);
            } finally {
                if (!cancelled) setLoadingLanguage(false);
            }
        };
        loadAndDecryptData();
        return () => {
            cancelled = true;
        };
    }, [selectedLanguage]);

    useEffect(() => {
        const raw = location.hash.replace(/^#/, "");
        if (!raw) return;
        const id = window.setTimeout(() => {
            document.getElementById(raw)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
        return () => window.clearTimeout(id);
    }, [location.pathname, location.hash]);

    const handleLanguageChange = (event, newLanguage) => {
        if (newLanguage !== null) setSelectedLanguage(newLanguage);
    };

    const handleCardClick = () => {
        navigate("/puzzles/crossword");
    };

    if (loadingLanguage || !samplePuzzle) {
        return (
            <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Typography>{t("howto.loading")}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Grid container spacing={0} sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Grid size={{ xs: 12 }} sx={{ width: "100%", maxWidth: "100%", padding: 0 }}>
                    <Item sx={{ p: { xs: 2, md: 4 }, textAlign: "left", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                        <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                            {t("stories.type-crossword")}
                        </Typography>
                        {SUPPORT_FRENCH ? (
                        <Box sx={{ mb: 4 }}>
                            <ToggleButtonGroup
                                value={selectedLanguage}
                                exclusive
                                onChange={handleLanguageChange}
                                aria-label="sample puzzle language"
                                sx={{
                                    "& .MuiToggleButton-root": {
                                        "&.Mui-selected": {
                                            borderColor: "#3BA666 !important",
                                            borderWidth: "2px !important",
                                            backgroundColor: "rgba(59, 166, 102, 0.08)",
                                            "&:hover": {
                                                backgroundColor: "rgba(59, 166, 102, 0.12)",
                                            },
                                        },
                                    },
                                }}
                            >
                                <Tooltip
                                    title={t("howto.tooltip-english")}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: "0.9rem",
                                                padding: "8px 12px",
                                                backgroundColor: "#3BA666",
                                                fontWeight: 500,
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                            },
                                        },
                                        arrow: { sx: { color: "#3BA666" } },
                                    }}
                                >
                                    <ToggleButton value="english" aria-label="english">
                                        {t("howto.english-example")}
                                    </ToggleButton>
                                </Tooltip>
                                <Tooltip
                                    title={t("howto.tooltip-french")}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: "0.9rem",
                                                padding: "8px 12px",
                                                backgroundColor: "#3BA666",
                                                fontWeight: 500,
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                            },
                                        },
                                        arrow: { sx: { color: "#3BA666" } },
                                    }}
                                >
                                    <ToggleButton value="french" aria-label="french">
                                        {t("howto.french-example")}
                                    </ToggleButton>
                                </Tooltip>
                            </ToggleButtonGroup>
                        </Box>
                        ) : null}

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.crossword.intro-title")}
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.intro-p1")}
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.intro-p2", { crossword: t("nav.crossword") })}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.crossword.puzzle-card-title")}
                            </Typography>
                            <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
                                <Box
                                    sx={{
                                        width: BROWSE_SHELF_CARD_WIDTH,
                                        maxWidth: "100%",
                                        flexShrink: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <Tooltip
                                        title={t("howto.crossword.card-dive-tooltip")}
                                        arrow
                                        placement="top"
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    fontSize: "0.95rem",
                                                    padding: "10px 14px",
                                                    backgroundColor: "#3BA666",
                                                    fontWeight: 500,
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                                },
                                            },
                                            arrow: { sx: { color: "#3BA666" } },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                cursor: "pointer",
                                                transition: "transform 0.2s ease-in-out",
                                                "&:hover": { transform: "translateY(-4px)" },
                                            }}
                                        >
                                            <BrowseStylePuzzleCard
                                                category="CROSSWORD"
                                                puzzle={samplePuzzle}
                                                storyTeaserRow={samplePuzzle}
                                                contentLanguage={
                                                    selectedLanguage === "french" ? "french" : "english"
                                                }
                                                lang={lang}
                                                t={t}
                                                userPayPlan="FREE"
                                                buttonText={t("howto.play")}
                                                forceUnlock={true}
                                                hideClusteredBubbles
                                                onPlayClick={handleCardClick}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.card-displays")}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-free-standard")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-title")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-level")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-words")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-published")}</Typography></li>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box id="play-main" sx={{ mb: 5, scrollMarginTop: "5rem" }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.crossword.play-main-title")}
                            </Typography>
                            {SUPPORT_FRENCH && selectedLanguage === "french" && (
                                <Alert severity="info" sx={{ mb: 3, maxWidth: "100%" }}>
                                    <Typography variant="body2" sx={{ fontSize: "0.95rem" }}>
                                        {t("howto.crossword.char-norm-info")}
                                    </Typography>
                                </Alert>
                            )}
                            <Box sx={{ mb: 3, display: "flex", justifyContent: "center", width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: "100%",
                                        border: `2px solid ${SURFACE.border}`,
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                        backgroundColor: SURFACE.sunken,
                                        position: "relative",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <StaticCrossPuzzleMain
                                        puzzleData={samplePuzzle}
                                        dictionaryLanguage={selectedLanguage}
                                        storyPlayLayout={false}
                                        isHowtoSampleDemo
                                    />
                                </Box>
                            </Box>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.play-main-parts")}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.part-title")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.part-grid")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.part-clues")}</Typography></li>
                            </Box>
                            <Box id="word-directions" sx={{ mt: 4, scrollMarginTop: "5rem" }}>
                                <WordDirectionsSection language={selectedLanguage} mode="crossword" embedded />
                            </Box>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box id="letter-boxes" sx={{ mb: 5, scrollMarginTop: "5rem" }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.crossword.letter-boxes-title", "Letter boxes")}
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t(
                                    "howto.crossword.letter-boxes-intro",
                                    "Select a word on the grid and a letter rack appears under it. You fill the word one letter at a time, not by typing the whole word into a single field.",
                                )}
                            </Typography>
                            <Box sx={{ mb: 3, maxWidth: 480 }}>
                                <CrosswordScrambleRack
                                    wordKey="howto-sail"
                                    expectedWord="SAIL"
                                    cells={[
                                        { row: 0, col: 0 },
                                        { row: 0, col: 1 },
                                        { row: 0, col: 2 },
                                        { row: 0, col: 3 },
                                    ]}
                                    meaningPreview={t(
                                        "howto.crossword.letter-boxes-sample-clue",
                                        "To travel on water using the wind",
                                    )}
                                    onLettersChange={() => {}}
                                    onCorrectPlacement={() => {}}
                                />
                            </Box>
                            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                <li>
                                    <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                        {t(
                                            "howto.crossword.letter-boxes-bank",
                                            "The top row holds the word’s letters, scrambled. Drag a letter, or tap it, to pick it up.",
                                        )}
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                        {t(
                                            "howto.crossword.letter-boxes-slots",
                                            "The bottom row is the answer. Each empty box is one letter of the word.",
                                        )}
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                        {t(
                                            "howto.crossword.letter-boxes-place",
                                            "Drop a letter into a box. A letter in the right box locks in. Fill every box correctly and the word is found.",
                                        )}
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                        {t(
                                            "howto.crossword.letter-boxes-tools",
                                            "Shuffle mixes the letters still in the top row. Reset sends placed letters back up.",
                                        )}
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                        {t(
                                            "howto.crossword.letter-boxes-clue",
                                            "The line under the boxes is the clue for the word you are filling.",
                                        )}
                                    </Typography>
                                </li>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <StaticCrossWordsPanelSection samplePuzzle={samplePuzzle} dictionaryLanguage={selectedLanguage} />
                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.crossword.toolbar-title")}
                            </Typography>
                            <Box sx={{ mb: 3, display: "flex", justifyContent: "center", width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                                <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                                    <StaticToolbar
                                        puzzleTitle={samplePuzzle?.title || "Sample Puzzle"}
                                        puzzleId={samplePuzzle?.id || "sample-id"}
                                        puzzleType={samplePuzzle?.type || "CROSSWORD"}
                                    />
                                </Box>
                            </Box>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.toolbar-intro")}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-back")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-bold")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-pdf")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-share")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-clear")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-full")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-clues")}</Typography></li>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.crossword.grid-title")}
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                                        <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                                            <StaticCrossGrid puzzleData={samplePuzzle} />
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                        {t("howto.crossword.grid-desc")}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                        <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.grid-across")}</Typography></li>
                                        <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.grid-down")}</Typography></li>
                                    </Box>
                                    <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                        {t("howto.crossword.grid-intersect")}
                                    </Typography>
                                    <Box sx={{ mt: 4 }}>
                                        <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                            {t("howto.crossword.progress-title")}
                                        </Typography>
                                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                            {t("howto.crossword.progress-desc")}
                                        </Typography>
                                        <Box sx={{ mb: 2, mt: 1 }}>
                                            <Paper sx={{ p: 1.5, backgroundColor: SURFACE.sunken, maxWidth: "400px" }}>
                                                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", gap: 2, mb: 1 }}>
                                                    <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600, letterSpacing: "0.5px", fontSize: "0.95rem" }}>
                                                        {t("nav.crossword")}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
                                                            (24 words)
                                                        </Box>
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                                                        {t("howto.crossword.find-all-words")}
                                                    </Typography>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                        <LinearProgress variant="determinate" value={Math.round((4 / 24) * 100)} sx={{ height: 4, borderRadius: 2, flex: 1 }} />
                                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: "fit-content", fontWeight: 500, fontSize: "0.9rem" }}>
                                                            4 / 24
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Item>
                </Grid>
            </Grid>
        </Box>
    );
}
